// ViewsBoost Diagnostics System
// Usage: Set VITE_VB_DIAG=1 in environment to enable

interface DiagEvent {
  t: number;
  tag: string;
  data?: any;
}

interface VBDiag {
  events: DiagEvent[];
  counters: Record<string, number>;
  groups: Record<string, { start: number; end?: number; duration?: number }>;
}

declare global {
  interface Window {
    __VB_DIAG__: VBDiag;
  }
}

// Initialize global diagnostic state
if (import.meta.env.VITE_VB_DIAG === '1' && typeof window !== 'undefined') {
  window.__VB_DIAG__ = {
    events: [],
    counters: {},
    groups: {}
  };
  console.log('🔍 VB DIAG ON');
}

const isDiagEnabled = () => import.meta.env.VITE_VB_DIAG === '1' && typeof window !== 'undefined';

/**
 * Log a diagnostic event
 */
export const on = (tag: string, data?: any) => {
  if (!isDiagEnabled() || !window.__VB_DIAG__) return;

  const event: DiagEvent = {
    t: performance.now(),
    tag,
    data
  };

  window.__VB_DIAG__.events.push(event);
  console.debug(`[VB] ${tag}`, data);
};

/**
 * Increment a counter
 */
export const count = (tag: string) => {
  if (!isDiagEnabled() || !window.__VB_DIAG__) return;

  if (!window.__VB_DIAG__.counters[tag]) {
    window.__VB_DIAG__.counters[tag] = 0;
  }
  window.__VB_DIAG__.counters[tag]++;

  on(`count:${tag}`, window.__VB_DIAG__.counters[tag]);
};

/**
 * Time a group of operations
 */
export const group = <T>(tag: string, fn: () => T): T => {
  if (!isDiagEnabled() || !window.__VB_DIAG__) {
    return fn();
  }

  const start = performance.now();
  performance.mark(`vb-${tag}-start`);

  window.__VB_DIAG__.groups[tag] = { start };
  on(`group:start:${tag}`);

  try {
    const result = fn();

    const end = performance.now();
    const duration = end - start;

    performance.mark(`vb-${tag}-end`);
    performance.measure(`vb-${tag}`, `vb-${tag}-start`, `vb-${tag}-end`);

    window.__VB_DIAG__.groups[tag] = { start, end, duration };
    on(`group:end:${tag}`, { duration });

    return result;
  } catch (error) {
    const end = performance.now();
    const duration = end - start;

    window.__VB_DIAG__.groups[tag] = { start, end, duration };
    on(`group:error:${tag}`, { duration, error });

    throw error;
  }
};

/**
 * Get current call stack (trimmed to app files)
 */
export const getStack = (skipFrames = 1): string => {
  if (!isDiagEnabled()) return '';
  
  const stack = new Error().stack || '';
  const lines = stack.split('\n');
  
  // Skip Error line + this function + caller frames
  const relevantLines = lines
    .slice(skipFrames + 2)
    .filter(line => line.includes('/src/'))
    .slice(0, 5) // Top 5 frames
    .map(line => {
      const match = line.match(/\/src\/([^:]+):(\d+)/);
      return match ? `${match[1]}:${match[2]}` : line.trim();
    });
  
  return relevantLines.join(' → ');
};

/**
 * Count active event listeners of specific types
 */
export const countListeners = (): Record<string, number> => {
  if (!isDiagEnabled() || !window.__VB_DIAG__) return {};

  const counts: Record<string, number> = {};

  // Check video elements
  const videos = document.querySelectorAll('video');
  videos.forEach((video, i) => {
    const events = ['timeupdate', 'loadedmetadata', 'error', 'canplay'];
    events.forEach(event => {
      // This is a rough estimate - we can't actually count listeners
      counts[`video-${event}`] = videos.length;
    });
  });

  on('listener-counts', counts);
  return counts;
};

/**
 * Export diagnostic data to JSON
 */
export const exportTrace = async (): Promise<void> => {
  if (!isDiagEnabled() || !window.__VB_DIAG__) return;

  const data = {
    ...window.__VB_DIAG__,
    exportTime: Date.now(),
    userAgent: navigator.userAgent,
    url: location.href
  };

  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = 'vb-trace.json';
  a.click();

  URL.revokeObjectURL(url);

  console.log('📊 Diagnostic data exported to vb-trace.json');
  console.log('Events:', data.events.length);
  console.log('Counters:', Object.keys(data.counters).length);
  console.log('Groups:', Object.keys(data.groups).length);
};

// Export as vb namespace
export const vb = {
  on,
  count,
  group,
  getStack,
  countListeners,
  exportTrace
};

export default vb;